import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { checkOpenAIAvailability } from '../../services/openaiService';

const OpenAIQuotaStatus = ({ className = '' }) => {
  const [quotaStatus, setQuotaStatus] = useState({
    available: true,
    reason: 'checking',
    message: 'Checking AI service...',
    lastChecked: null
  });
  const [isChecking, setIsChecking] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const checkQuotaStatus = async () => {
    setIsChecking(true);
    try {
      const status = await checkOpenAIAvailability();
      setQuotaStatus({
        ...status,
        lastChecked: new Date()?.toLocaleTimeString()
      });
    } catch (error) {
      setQuotaStatus({
        available: false,
        reason: 'error',
        message: 'Unable to check AI service status',
        lastChecked: new Date()?.toLocaleTimeString()
      });
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkQuotaStatus();
    
    // Check status every 5 minutes
    const interval = setInterval(checkQuotaStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = () => {
    if (isChecking) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (quotaStatus?.available) return 'text-green-600 bg-green-50 border-green-200';
    
    switch (quotaStatus?.reason) {
      case 'quota_exceeded':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'rate_limited':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'no_api_key': case'auth_error':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  const getStatusIcon = () => {
    if (isChecking) return 'Loader';
    if (quotaStatus?.available) return 'CheckCircle';
    
    switch (quotaStatus?.reason) {
      case 'quota_exceeded':
        return 'AlertTriangle';
      case 'rate_limited':
        return 'Clock';
      case 'no_api_key': case'auth_error':
        return 'Key';
      default:
        return 'XCircle';
    }
  };

  const getStatusMessage = () => {
    if (isChecking) return 'Checking...';
    if (quotaStatus?.available) return 'AI parsing available';
    
    switch (quotaStatus?.reason) {
      case 'quota_exceeded':
        return 'Quota exceeded - using fallback parsing';
      case 'rate_limited':
        return 'Rate limited - please wait';
      case 'no_api_key':
        return 'API key not configured';
      case 'auth_error':
        return 'Authentication issue';
      default:
        return 'AI parsing unavailable';
    }
  };

  const getRecommendation = () => {
    switch (quotaStatus?.reason) {
      case 'quota_exceeded':
        return 'Your OpenAI quota has been exceeded. Files will be imported using basic parsing. Check your OpenAI billing to restore AI features.';
      case 'rate_limited':
        return 'Too many requests. Please wait a few minutes before trying again.';
      case 'no_api_key':
        return 'OpenAI API key is not configured. Contact your administrator to enable AI features.';
      case 'auth_error':
        return 'OpenAI API key is invalid. Please check your configuration.';
      default:
        return 'AI parsing is temporarily unavailable. Basic parsing will be used instead.';
    }
  };

  return (
    <div className={`inline-flex items-center ${className}`}>
      <div 
        className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-all ${getStatusColor()}`}
        onClick={() => setShowDetails(!showDetails)}
      >
        <Icon 
          name={getStatusIcon()} 
          size={14} 
          className={`mr-2 ${isChecking ? 'animate-spin' : ''}`} 
        />
        <span>{getStatusMessage()}</span>
        {quotaStatus?.lastChecked && (
          <span className="ml-2 text-xs opacity-70">
            ({quotaStatus?.lastChecked})
          </span>
        )}
        <Icon 
          name={showDetails ? 'ChevronUp' : 'ChevronDown'} 
          size={14} 
          className="ml-1" 
        />
      </div>
      
      {showDetails && (
        <div className="absolute z-10 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
          <div className="flex items-start space-x-3">
            <Icon 
              name={getStatusIcon()} 
              size={20} 
              className={quotaStatus?.available ? 'text-green-600' : 'text-red-600'} 
            />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900">
                AI Service Status
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                {quotaStatus?.message}
              </p>
              {!quotaStatus?.available && (
                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-700">
                    <strong>What this means:</strong> {getRecommendation()}
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={checkQuotaStatus}
                  disabled={isChecking}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                >
                  {isChecking ? 'Checking...' : 'Refresh Status'}
                </button>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenAIQuotaStatus;